import { useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import { motion } from "framer-motion";
import { TrendingUp, ShoppingBag, Receipt, Boxes } from "lucide-react";
import { DASHBOARD } from "@/config";
import { Reveal, stagger, fadeUp } from "@/components/Reveal";

const FILTERS = ["This Week", "Last Week", "This Month", "Last Month", "Last 30 Days", "Last 90 Days", "Custom Range"];
const PIE_COLORS = ["hsl(24,14%,20%)", "#6abb32", "hsl(95,28%,34%)", "hsl(140,24%,52%)", "hsl(200,55%,55%)"];

const KPIS = [
  { icon: TrendingUp, label: "Spending This Month", value: "$8,742", sub: "+6.1% vs last month" },
  { icon: ShoppingBag, label: "Average Order Value", value: "$486", sub: "18 orders placed" },
  { icon: Receipt, label: "Open Invoices", value: "3", sub: "All current" },
  { icon: Boxes, label: "Unique SKUs Ordered", value: "64", sub: "Across 6 categories" },
];

export default function ReportingDashboard({ withFilters = true }) {
  const [active, setActive] = useState("This Month");
  return (
    <div>
      {withFilters && (
        <Reveal className="flex flex-wrap gap-2 mb-8">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setActive(f)}
              data-testid={`report-filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-xs font-medium rounded-full px-3.5 py-2 border transition-colors duration-200 ${
                active === f ? "bg-forest text-butter border-forest" : "border-forest/20 text-forest/70 hover:border-forest/50"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>
      )}

      {/* KPI cards */}
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {KPIS.map(({ icon: Icon, label, value, sub }) => (
          <motion.div key={label} variants={fadeUp} className="rounded-2xl bg-white border border-forest/15 p-5">
            <div className="flex items-center justify-between">
              <span className="grid place-items-center h-9 w-9 rounded-lg bg-sage text-forest"><Icon className="h-4.5 w-4.5" /></span>
            </div>
            <p className="font-display text-3xl text-forest mt-3">{value}</p>
            <p className="text-xs text-forest/60 mt-1">{label}</p>
            <p className="text-[11px] text-forest/45 mt-1 font-mono-accent">{sub}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Reveal className="lg:col-span-2 rounded-2xl bg-white border border-forest/15 p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-display text-lg text-forest">Monthly Purchasing Trend</h4>
            <span className="overline text-forest/45">CAD</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={180} initialDimension={{ width: 800, height: 200 }}>
              <AreaChart data={DASHBOARD.spendTrend} margin={{ left: -18, right: 6, top: 6 }}>
                <defs>
                  <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6abb32" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#6abb32" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(24,12%,86%)" vertical={false} />
                <XAxis dataKey="m" stroke="hsl(24,10%,40%)" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(24,10%,40%)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(24 12% 82%)", fontSize: 12 }} />
                <Area type="monotone" dataKey="v" stroke="hsl(24,14%,18%)" strokeWidth={2.5} fill="url(#spendFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Reveal>

        <Reveal delay={0.05} className="rounded-2xl bg-white border border-forest/15 p-5">
          <h4 className="font-display text-lg text-forest mb-4">Spending by Category</h4>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={180} initialDimension={{ width: 800, height: 200 }}>
              <PieChart>
                <Pie data={DASHBOARD.byCategory} dataKey="value" nameKey="name" innerRadius={45} outerRadius={78} paddingAngle={3}>
                  {DASHBOARD.byCategory.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(150 40% 82%)", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-2">
            {DASHBOARD.byCategory.map((c, i) => (
              <span key={c.name} className="inline-flex items-center gap-1.5 text-[11px] text-forest/70">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i] }} /> {c.name}
              </span>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Top items */}
      <Reveal className="rounded-2xl bg-white border border-forest/15 p-5 mt-4">
        <h4 className="font-display text-lg text-forest mb-4">Most Ordered Items</h4>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={180} initialDimension={{ width: 800, height: 200 }}>
            <BarChart data={DASHBOARD.topItems} layout="vertical" margin={{ left: 40, right: 12 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={130} stroke="hsl(24,10%,40%)" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip cursor={{ fill: "hsl(38,25%,90%)" }} contentStyle={{ borderRadius: 12, border: "1px solid hsl(24 12% 82%)", fontSize: 12 }} />
              <Bar dataKey="qty" fill="#6abb32" radius={[0, 8, 8, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Reveal>
    </div>
  );
}
